import type { IDashboardRepository } from "../repositories/DashboardRepository";
import type {
  DashboardOverview,
  SponsorDataManagement,
  SponsorEnrollmentAnalytics,
  SponsorFinancialOverview,
  SponsorMilestones,
  SponsorMonitoringSummary,
  SponsorStudyInfo,
  SponsorSaes,
  SponsorAlerts,
  IwrsDashboardData,
} from "../types/dashboard.types";

export interface IDashboardUseCase {
  getOverview(feasibleSitesOnly?: boolean): Promise<DashboardOverview>;
  getSponsorStudyInfo(
    studyId?: string,
    feasibleSitesOnly?: boolean,
  ): Promise<SponsorStudyInfo>;
  getSponsorEnrollmentAnalytics(
    studyId?: string,
    feasibleSitesOnly?: boolean,
  ): Promise<SponsorEnrollmentAnalytics>;
  getSponsorMonitoringSummary(
    studyId?: string,
    feasibleSitesOnly?: boolean,
  ): Promise<SponsorMonitoringSummary>;
  getSponsorDataManagement(studyId?: string): Promise<SponsorDataManagement>;
  getSponsorMilestones(
    studyId?: string,
    feasibleSitesOnly?: boolean,
  ): Promise<SponsorMilestones>;
  getSponsorFinancialOverview(
    studyId?: string,
    feasibleSitesOnly?: boolean,
  ): Promise<SponsorFinancialOverview>;
  getSponsorSaes(studyId?: string): Promise<SponsorSaes>;
  getSponsorAlerts(
    studyId?: string,
    feasibleSitesOnly?: boolean,
  ): Promise<SponsorAlerts>;
  getSponsorIwrsData(studyId?: string): Promise<IwrsDashboardData>;
}

export class DashboardUseCase implements IDashboardUseCase {
  constructor(private readonly repo: IDashboardRepository) {}

  async getOverview(feasibleSitesOnly = false): Promise<DashboardOverview> {
    return this.repo.getOverview(feasibleSitesOnly);
  }

  async getSponsorStudyInfo(
    studyId?: string,
    feasibleSitesOnly = false,
  ): Promise<SponsorStudyInfo> {
    return this.repo.getSponsorStudyInfo(studyId, feasibleSitesOnly);
  }

  async getSponsorEnrollmentAnalytics(
    studyId?: string,
    feasibleSitesOnly = false,
  ): Promise<SponsorEnrollmentAnalytics> {
    return this.repo.getSponsorEnrollmentAnalytics(studyId, feasibleSitesOnly);
  }

  async getSponsorMonitoringSummary(
    studyId?: string,
    feasibleSitesOnly = false,
  ): Promise<SponsorMonitoringSummary> {
    return this.repo.getSponsorMonitoringSummary(studyId, feasibleSitesOnly);
  }

  async getSponsorDataManagement(
    studyId?: string,
  ): Promise<SponsorDataManagement> {
    return this.repo.getSponsorDataManagement(studyId);
  }

  async getSponsorMilestones(
    studyId?: string,
    feasibleSitesOnly = false,
  ): Promise<SponsorMilestones> {
    return this.repo.getSponsorMilestones(studyId, feasibleSitesOnly);
  }

  async getSponsorFinancialOverview(
    studyId?: string,
    feasibleSitesOnly = false,
  ): Promise<SponsorFinancialOverview> {
    return this.repo.getSponsorFinancialOverview(studyId, feasibleSitesOnly);
  }

  async getSponsorSaes(studyId?: string): Promise<SponsorSaes> {
    return this.repo.getSponsorSaes(studyId);
  }

  async getSponsorAlerts(
    studyId?: string,
    feasibleSitesOnly = false,
  ): Promise<SponsorAlerts> {
    return this.repo.getSponsorAlerts(studyId, feasibleSitesOnly);
  }

  async getSponsorIwrsData(studyId?: string): Promise<IwrsDashboardData> {
    return this.repo.getSponsorIwrsData(studyId);
  }
}
